import { register } from "./registry";

register((root) => {
  // Every page, form or list, opens with the cursor in its first field; native autofocus loses to an already-focused element.
  const autofocusEl = root.querySelector<HTMLElement>("[autofocus]");
  // The bundle runs after the stylesheets load; by then a fast typist may already be typing into the natively autofocused
  // box, and select() would wipe what they typed.
  const typedInto = autofocusEl === document.activeElement && autofocusEl instanceof HTMLInputElement && autofocusEl.value !== autofocusEl.defaultValue;
  if (autofocusEl && !typedInto) focusField(autofocusEl);

  const forms = root.querySelectorAll<HTMLFormElement>("[data-kit-form]");
  if (forms.length === 0) return;

  forms.forEach((form) => {
    // A lookup inside the form (ledger info, totals, row fetch) must not inherit the save's hx-disabled-elt or hx-swap:
    // the first disabled Save while the lookup ran (a quick Ctrl+S was lost), the second ("none") dropped the lookup's result.
    if (!form.hasAttribute("hx-disinherit")) form.setAttribute("hx-disinherit", "hx-disabled-elt hx-swap");
    form.addEventListener("keydown", (e: Event) => {
      const ev = e as KeyboardEvent;
      if (ev.key === "Enter" && (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLSelectElement || ev.target instanceof HTMLTextAreaElement)) {
        if (ev.target instanceof HTMLInputElement && ev.target.type === "submit") return;
        if (ev.target.matches("input[data-kit-ac]")) return; // autocomplete handles Enter! (it binds inputs only; a textarea moves on below)
        if ((ev.target as HTMLElement).dataset.kitOwnsEnter === "1") { ev.preventDefault(); return; } // owner handles Enter; never an implicit form submit
        if (ev.target.closest("[data-kit-grid]")) return; // handled by entry-grid.ts!
        if (ev.target.hasAttribute("data-kit-submit-on-enter")) {
          ev.preventDefault();
          const targetForm = (ev.target as HTMLElement).closest("form");
          if (targetForm) targetForm.requestSubmit();
          return;
        }
        ev.preventDefault();
        moveFocus(ev.target, ev.shiftKey ? -1 : 1);
      }
    });

    const buttons = form.querySelectorAll("button");
    for (const b of buttons) {
      if (b.type !== "submit" && !b.hasAttribute("tabindex")) b.setAttribute("tabindex", "-1");
    }

    const saveBtn = form.querySelector("[data-kit-save]") as HTMLButtonElement | null;
    if (saveBtn) {
      form.addEventListener("htmx:configRequest", (event) => { if (event.target === form) saveBtn.disabled = true; });
      form.addEventListener("htmx:afterRequest", (event) => { if (event.target === form) saveBtn.disabled = false; });
    }
  });
});

export function moveFocus(current: Element, dir: number) {
  const container: ParentNode = current.closest("form") ?? current.closest("dialog") ?? document;
  const all = Array.from(container.querySelectorAll<HTMLElement>("[data-kit-order]")).filter(
    (el) => {
      if (el.hidden || el.hasAttribute("hidden")) return false;
      if (el instanceof HTMLInputElement && el.type === "hidden") return false;
      if ((el as HTMLInputElement).disabled) return false;
      if (el.closest("[style*='display: none'], [style*='display:none'], [hidden]")) return false;
      if (el.closest(".list-filters:not(.is-open) .list-more")) return false; // folded "More filters" (ui-lists.css)
      return true;
    }
  ).sort(
    (a, b) => parseInt(a.getAttribute("data-kit-order") || "0") - parseInt(b.getAttribute("data-kit-order") || "0")
  );
  const idx = all.indexOf(current as HTMLElement);
  if (idx > -1 && idx + dir >= 0 && idx + dir < all.length) {
    const next = all[idx + dir];
    if (next?.matches("[data-kit-grid]")) {
      const grid = next as HTMLTableElement;
      const firstCell = grid.querySelector<HTMLElement>("tbody tr [data-kit-col]:not([readonly]):not([disabled]):not([tabindex='-1']), tbody tr input:not([readonly]):not([disabled]):not([type='hidden']):not([tabindex='-1']), tbody tr select:not([readonly]):not([disabled]):not([tabindex='-1'])");
      if (firstCell) {
        focusField(firstCell);
        return;
      }
    }
    focusField(next);
  } else if (idx === all.length - 1 && dir > 0) {
    const firstRow = document.querySelector<HTMLElement>("[data-kit-list-row]");
    if (firstRow) focusField(firstRow);
  }
}

export function focusField(el: HTMLElement | undefined | null): void {
  if (el) { el.focus(); if (el instanceof HTMLInputElement) el.select(); }
}
